import { create } from "zustand";
import PartnerProfile, {
  OpeningTimes,
  Photo,
  PartnerMassageService,
} from "../../user/partner/PartnerProfile";
import { GenderTypeKey } from "../../user/partner/Gender";
import { WorkplaceKey } from "../../user/partner/Workplace";
import { AvailabilityKey } from "../../user/partner/Availability";
import AddPartnerProfileFormWorker from "../../user/partner/AddPartnerProfileFormWorker";
import { MassageCategoryKey } from "../../user/partner/MassageCategoryKey";
import { ClientTypeKey } from "../../user/client/ClientType";
import { PaymentMethodKey } from "../../user/partner/PaymentMethod";
import { ImageFile } from "../../components/AnnouncementForm/AnouncementFormSteps/InformationForm/ImagesUpload/ImagesUpload";
import { Place } from "../../utils/interfaces/announcementForm";
import User from "../../user/User";

interface AddPartnerProfileStore {
  existingPhotos?: Photo[];
  setExistingPhotos: (photos: Photo[]) => void;

  photos?: ImageFile[];
  setPhotos: (photos?: ImageFile[]) => void;
  photosToAdd?: ImageFile[];
  setPhotosToAdd: (photosToAdd?: ImageFile[]) => void;
  photosToDelete?: Photo[];
  setPhotosToDelete: (photosToDelete?: Photo[]) => void;
  name?: string;
  setName: (name?: string) => void;
  gender?: GenderTypeKey;
  setGender: (gender?: GenderTypeKey) => void;
  description?: string;
  setDescription: (description?: string) => void;
  engDescription?: string;
  setEngDescription: (engDescription?: string) => void;

  city?: string;
  setCity: (city?: string) => void;

  cityDescription?: string;
  setCityDescription: (cityDescription?: string) => void;

  place?: Place;
  setPlace: (place?: Place) => void;

  district?: string;
  setDistrict: (district?: string) => void;
  neighbourhood?: string;
  setNeighbourhood: (neighbourhood?: string) => void;
  street?: string;
  setStreet: (street?: string) => void;
  houseNumber?: string;
  setHouseNumber: (houseNumber?: string) => void;
  postCode?: string;
  setPostCode: (postCode?: string) => void;
  isVisitingClient?: boolean;
  setIsVisitingClient: (isVisitingClient?: boolean) => void;
  workplace?: WorkplaceKey;
  setWorkplace: (workplaceKey?: WorkplaceKey) => void;
  availability?: AvailabilityKey[];
  setAvailability: (availability?: AvailabilityKey[]) => void;
  openingTimes?: OpeningTimes;
  setOpeningTimes: (openingTimes?: OpeningTimes) => void;
  workers?: AddPartnerProfileFormWorker[];
  setWorkers: (workers?: AddPartnerProfileFormWorker[]) => void;
  workersToAdd?: AddPartnerProfileFormWorker[];
  setWorkersToAdd: (workers?: AddPartnerProfileFormWorker[]) => void;
  workersToDelete?: AddPartnerProfileFormWorker[];
  setWorkersToDelete: (workers?: AddPartnerProfileFormWorker[]) => void;
  workersToEdit?: AddPartnerProfileFormWorker[];
  setWorkersToEdit: (workers?: AddPartnerProfileFormWorker[]) => void;
  serviceCategory?: MassageCategoryKey;
  setServiceCategory: (serviceCategory?: MassageCategoryKey) => void;
  services?: PartnerMassageService[];
  setServices: (services?: PartnerMassageService[]) => void;
  currency?: string;
  setCurrency: (currency?: string) => void;
  isTechnician?: boolean;
  setIsTechnician: (isTechnician?: boolean) => void;
  clientTypes?: ClientTypeKey[];
  setClientTypes: (type?: ClientTypeKey[]) => void;
  paymentMethods?: PaymentMethodKey[];
  setPaymentMethods: (paymentMethod?: PaymentMethodKey[]) => void;
  phonePrefix?: string;
  setPhonePrefix: (phonePrefix?: string) => void;
  phoneNumber?: string;
  setPhoneNumber: (phoneNumber?: string) => void;
  email?: string;
  setEmail: (email?: string) => void;
  website?: string;
  setWebsite: (website?: string) => void;
  facebook?: string;
  setFacebook: (facebook?: string) => void;
  instagram?: string;
  setInstagram: (instagram?: string) => void;
  youtube?: string;
  setYoutube: (youtube?: string) => void;
  isAgreementAccepted?: boolean;
  setIsAgreementAccepted: (isAgreementAccepted?: boolean) => void;
  breakTime?: string;
  setBreakTime: (breakTime?: string) => void;
  isCalendarEnabled: boolean;
  setIsCalendarEnabled: (isCalendarEnabled: boolean) => void;
  isEditing: boolean;
  setIsEditing: (isEditing: boolean) => void;
}

export const useAddPartnerStore = create<AddPartnerProfileStore>((set) => ({
  existingPhotos: [],
  setExistingPhotos: (existingPhotos: Photo[]) => set({ existingPhotos }),

  photos: [],
  setPhotos: (photos) => set({ photos }),

  photosToAdd: [],
  setPhotosToAdd: (photosToAdd) => set({ photosToAdd }),

  photosToDelete: [],
  setPhotosToDelete: (photosToDelete) => set({ photosToDelete }),

  name: "",
  setName: (name) => set({ name }),

  gender: undefined,
  setGender: (gender) => set({ gender }),

  description: "",
  setDescription: (description) => set({ description }),

  engDescription: "",
  setEngDescription: (engDescription) => set({ engDescription }),

  city: "",
  setCity: (city) => set({ city }),

  cityDescription: "",
  setCityDescription: (cityDescription) => set({ cityDescription }),

  place: undefined,
  setPlace: (place) => set({ place }),

  district: "",
  setDistrict: (district) => set({ district }),

  neighbourhood: "",
  setNeighbourhood: (neighbourhood) => set({ neighbourhood }),

  street: "",
  setStreet: (street) => set({ street }),

  houseNumber: "",
  setHouseNumber: (houseNumber) => set({ houseNumber }),

  postCode: "",
  setPostCode: (postCode) => set({ postCode }),

  isVisitingClient: false,
  setIsVisitingClient: (isVisitingClient) => set({ isVisitingClient }),

  workplace: undefined,
  setWorkplace: (workplace) => set({ workplace }),

  availability: [],
  setAvailability: (availability) => set({ availability }),

  openingTimes: undefined,
  setOpeningTimes: (openingTimes) => set({ openingTimes }),

  workers: [],
  setWorkers: (workers) => set({ workers }),

  workersToAdd: [],
  setWorkersToAdd: (workersToAdd) => set({ workersToAdd }),

  workersToDelete: [],
  setWorkersToDelete: (workersToDelete) => set({ workersToDelete }),

  workersToEdit: [],
  setWorkersToEdit: (workersToEdit) => set({ workersToEdit }),

  serviceCategory: undefined,
  setServiceCategory: (serviceCategory) => set({ serviceCategory }),

  services: [],
  setServices: (services) => set({ services }),

  currency: "",
  setCurrency: (currency) => set({ currency }),

  isTechnician: false,
  setIsTechnician: (isTechnician) => set({ isTechnician }),

  clientTypes: [],
  setClientTypes: (clientTypes) => set({ clientTypes }),

  paymentMethods: [],
  setPaymentMethods: (paymentMethods) => set({ paymentMethods }),

  phonePrefix: "",
  setPhonePrefix: (phonePrefix) => set({ phonePrefix }),

  phoneNumber: "",
  setPhoneNumber: (phoneNumber) => set({ phoneNumber }),

  email: "",
  setEmail: (email) => set({ email }),

  website: "",
  setWebsite: (website) => set({ website }),

  facebook: "",
  setFacebook: (facebook) => set({ facebook }),

  instagram: "",
  setInstagram: (instagram) => set({ instagram }),

  youtube: "",
  setYoutube: (youtube) => set({ youtube }),

  isAgreementAccepted: false,
  setIsAgreementAccepted: (isAgreementAccepted) => set({ isAgreementAccepted }),

  breakTime: "",
  setBreakTime: (breakTime) => set({ breakTime }),

  isCalendarEnabled: false,
  setIsCalendarEnabled: (isCalendarEnabled) => set({ isCalendarEnabled }),

  isEditing: false,
  setIsEditing: (isEditing) => set({ isEditing }),
}));

export const useAddPartnerStoreSetup = () => {
  const store = useAddPartnerStore();

  const setDefaultData = (partner?: PartnerProfile, user?: User | null) => {
    store.setExistingPhotos(partner?.photos || []);
    store.setPhotos([]);

    store.setName(partner?.name || "");
    store.setGender(partner?.gender);
    store.setDescription(partner?.description || "");
    store.setEngDescription(partner?.engDescription || "");

    store.setCity(partner?.city || partner?.place?.placeId || "");
    store.setCityDescription(
      partner?.cityDescription || partner?.place?.city || ""
    );
    store.setPlace(partner?.place);

    store.setDistrict(partner?.district || "");
    store.setNeighbourhood(partner?.neighbourhood || "");
    store.setStreet(partner?.street || "");
    store.setHouseNumber(partner?.houseNumber || "");
    store.setPostCode(partner?.postCode || "");
    store.setIsVisitingClient(!!partner?.isVisitingClient);
    store.setWorkplace(partner?.workplace);
    store.setAvailability(partner?.availability || []);
    store.setOpeningTimes(partner?.openingTimes);
    store.setWorkers(partner?.workers || []);
    store.setServiceCategory(partner?.serviceCategory);
    store.setServices(partner?.services || []);
    store.setCurrency(partner?.currency || "");
    store.setIsTechnician(partner?.isTechnician);
    store.setClientTypes(partner?.clientTypes || []);
    store.setPaymentMethods(partner?.paymentMethods || []);
    store.setPhonePrefix(partner?.phonePrefix || user?.phonePrefix || "");
    store.setPhoneNumber(partner?.phoneNumber || user?.phoneNumber || "");
    store.setEmail(partner?.email || user?.email || "");
    store.setWebsite(partner?.website || "");
    store.setFacebook(partner?.facebook || "");
    store.setInstagram(partner?.instagram || "");
    store.setYoutube(partner?.youtube || "");
    store.setBreakTime(partner?.breakTime?.toString() || "");
    store.setIsCalendarEnabled(!!partner?.isCalendarEnabled);
  };

  return { setDefaultData };
};
